<template>
  <div
    :class="[
      'tw:flex tw:min-h-[40px] tw:items-center tw:gap-4 tw:rounded-sm tw:border tw:border-solid tw:border-denim-300 tw:px-2 tw:text-xs tw:font-normal',
      { 'tw:cursor-not-allowed tw:opacity-50': removalPeriod.isArchived },
    ]"
    data-testid="manage-social-connections-hashtag-card"
  >
    <Checkbox
      class="tw:h-[18px]!"
      data-testid="checkbox"
      dense
      :disabled="removalPeriod.isArchived"
      :model-value="isChecked"
      new-design
      @update:model-value="checkboxClicked"
    />

    <BaseIcon
      class="tw:text-lg tw:text-gray-500"
      icon="hashtag"
    />

    <p class="tw:mb-0 tw:truncate tw:text-sm tw:font-bold tw:text-denim-900">
      {{ instagramHashtag.hashtag }}
    </p>

    <BaseButton
      :aria-label="
        !removalPeriod.isArchived ? getLocaleText('global.delete') : undefined
      "
      :class="[
        'tw:ml-auto tw:whitespace-nowrap tw:text-blue-900',
        {
          'tw:pr-[5px]!': removalPeriod.isArchived,
        },
      ]"
      data-testid="delete-button"
      :disabled="removalPeriod.isArchived"
      flat
      icon="delete_outline"
      icon-right
      :label="removalPeriod.timeLeft"
      new-design
      @click="emit('delete-clicked', props.id)"
    />
    <StreemTooltip v-if="tooltip">{{ tooltip }}</StreemTooltip>
  </div>
</template>

<script setup lang="ts">
import { type UnwrapRef, computed, inject } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseIcon, StreemTooltip } from "shared/components/base";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import { INSTAGRAM_MONITOR_COOLING_PERIOD_MS } from "shared/constants/social";
import { timeUntil } from "shared/helpers/date";
import {
  type UseHashtagContextReturn,
  HASHTAG_CONTEXT,
} from "shared/modals/ManageSocialConnections/useHashtagContext";
import type InstagramHashtagsOrganisationSocialIntegration from "shared/resources/InstagramHashtagsOrganisationSocialIntegration";

type Id = NonNullable<
  InstagramHashtagsOrganisationSocialIntegration["instagramHashtagId"]
>;
interface ManageSocialConnectionsHashtagCardProps {
  instagramHashtag: UnwrapRef<InstagramHashtagsOrganisationSocialIntegration>;
  id: Id;
}

type RemovalPeriod = {
  isArchived: boolean;
  timeLeft: string;
};

const props = defineProps<ManageSocialConnectionsHashtagCardProps>();

const { hashtagIsSelected } = inject<UseHashtagContextReturn>(HASHTAG_CONTEXT)!;

const removalPeriod = computed<RemovalPeriod>(() => {
  const archivedAt = props.instagramHashtag?.archivedAt ?? null;

  return {
    isArchived: Boolean(archivedAt),
    timeLeft: archivedAt
      ? removedIn(timeUntil(archivedAt, INSTAGRAM_MONITOR_COOLING_PERIOD_MS))
      : "",
  };
});

const isChecked = computed(() => {
  const isSelected = hashtagIsSelected(props.id);

  return removalPeriod.value.isArchived ? false : isSelected;
});

const tooltip = computed(() =>
  removalPeriod.value.isArchived
    ? getLocaleText(
        "manage_social_connections.hashtags_limitation_cooloff_tooltip"
      )
    : ""
);

function removedIn(timeUntilRemoval: string): string {
  return getLocaleText("manage_social_connections.hashtag_cooling_period", {
    period: timeUntilRemoval,
  });
}

function checkboxClicked(checked: boolean): void {
  emit("checkbox-clicked", props.id, checked);
}

const emit = defineEmits<{
  "checkbox-clicked": [Id, boolean];
  "delete-clicked": [Id];
}>();
</script>
